import { compact } from '@leland-dev/leland-ui-library';
import { isEmpty, isNil, omitBy } from 'lodash-es';
import { type ReadonlyURLSearchParams } from 'next/dist/client/components/navigation';
import type { UrlObject } from 'url';

import { type ReferralType } from '../__generated-gql-types__/globalTypes';

import { APPLICANT_SITE_URL } from './constants';
import { REFERRAL_TYPE_MAP } from './referrals';
import { LELAND_PLUS_REFERRING_COACH_KEY, LelandLocalStorage } from './storage';
// we don't want to pass around routers.
// but we want to get the login path with a redirect back and carry any existing search params.
export const getLoginRedirectString = (
  path: string | null,
  searchParams = {},
) => {
  const newParams = {
    ...searchParams,
    redirect_url: `${process.env.NEXT_PUBLIC_LELAND_APPLICANT_URL}${path}`,
  };
  const url = getUrlString(
    '/login',
    newParams,
    process.env.NEXT_PUBLIC_LELAND_APPLICANT_URL,
  );
  return url;
};

export const getSignupRedirectString = (redirectUrl?: string): string => {
  return getUrlString(
    `${process.env.NEXT_PUBLIC_LELAND_APPLICANT_URL}/signup`,
    {
      redirect_url: redirectUrl ?? '',
    },
  );
};

export const getLoginUrlString = (
  searchParams: Record<string, unknown>,
): string => {
  return getUrlString(
    `${process.env.NEXT_PUBLIC_LELAND_APPLICANT_URL}/login`,
    searchParams,
  );
};

// weird typeform use case that builds none spec url
export const getHashUrlString = (
  url: string,
  query: UrlObject['query'],
): string => {
  if (isEmpty(query)) {
    return url;
  }

  /* eslint-disable @typescript-eslint/no-unsafe-argument */
  const paramsString = Object.entries(omitBy(query, isNil))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value
          .filter(compact)
          .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
          .join('&');
      } else {
        return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
    })
    .join('&');
  /* eslint-enable @typescript-eslint/no-unsafe-argument */

  const char = '#';
  return `${url}${url.includes(char) ? '&' : char}${paramsString}`;
};

// this should be the default case for almost all next navigation. We want strings to work with the new router API not objects.
export const getUrlString = (
  url: string,
  query?: Record<string, unknown> | ReadonlyURLSearchParams,
  rootUrl = process.env.NEXT_PUBLIC_LELAND_APPLICANT_URL,
): string => {
  const properUrl = new URL(`${rootUrl}${url}`);

  if (query) {
    for (const key in query) {
      if (query[key]) {
        properUrl.searchParams.set(key, query[key] as string);
      }
    }
  }

  return properUrl.href;
};

// working with url search params objects everywhere is tedious. Let's just convert them to an object, and then we'll convert them back in the getUrlString function before use.
export const convertUrlSearchParamsToObject = (
  searchParams: ReadonlyURLSearchParams | URLSearchParams,
): Record<string, string> => {
  const obj: Record<string, string> = {};
  searchParams.forEach((value, key) => {
    if (value) {
      obj[key] = value;
    }
  });
  return obj;
};

// take an object of any type and safely convert it to a URLSearchParams object.
export const convertObjectToSearchParams = (
  searchParams: Record<string, unknown>,
): URLSearchParams => {
  const urlSearchParams = new URLSearchParams();
  for (const key in searchParams) {
    // we can't hand it undefined/nulls or it'll convert those to strings by default and break things.
    if (searchParams[key]) {
      urlSearchParams.set(key, searchParams[key] as string);
    }
  }
  return urlSearchParams;
};

//payment link comes in as a search param.
export const getStripePaymentLinkObject = (
  searchParams: ReadonlyURLSearchParams | URLSearchParams,
  email: string,
): string | undefined => {
  if (searchParams?.get('payment_link')) {
    if (searchParams.get('coach')) {
      LelandLocalStorage.setItem(
        LELAND_PLUS_REFERRING_COACH_KEY,
        searchParams.get('coach'),
      );
    }
    return getUrlString(searchParams.get('payment_link') || '', {
      prefilled_email: email,
    });
  }
};

export const getCustomerReferralUrl = (
  referralCode: string,
  referralType: ReferralType,
): string => {
  return `${APPLICANT_SITE_URL}/signup?referralCode=${referralCode}&referralType=${REFERRAL_TYPE_MAP[referralType]}`;
};
